// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import Layout from '@components/common/layout';
import Loader from '@components/common/loader';
import meta, { REVALIDATE_TIMEOUT } from '@config/constants/meta-info';
import { GuideObject } from '@lib/api';
import { PageProps } from '@pages/_app';
import { GuidesAPI } from '@services/api';
import GuideItemView from '@views/guides/guide';

export type GuideProps = InferGetStaticPropsType<typeof getStaticProps>;

export const getStaticPaths: GetStaticPaths = async () => {
  const response = await GuidesAPI.getGuides();
  const paths = response.data.flatMap((category) =>
    category.records.map((guide) => ({
      params: {
        id: `${guide.id}-${_.kebabCase(guide.title)}`,
      },
    })),
  );

  return {
    paths,
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps<
  { guide?: GuideObject },
  { id: string }
> = async (context) => {
  const id = parseInt(context.params?.id || '', 10);

  if (id) {
    const response = await GuidesAPI.getOneGuide({
      guide: id,
    });

    return {
      props: {
        guide: response.data,
      },
      revalidate: REVALIDATE_TIMEOUT,
    };
  }

  return {
    props: {},
    revalidate: REVALIDATE_TIMEOUT,
  };
};

interface Props extends GuideProps, PageProps {}

const GuideItem: React.FC<Props> = ({ guide, frontUrl }) => {
  const router = useRouter();

  useEffect(() => {
    if (!guide) {
      router.replace('/guides');
    }
  }, [guide, router]);

  return (
    <>
      <NextSeo
        title={guide?.metaTitle || meta.home.title}
        description={guide?.metaDescription || meta.home.description}
        openGraph={{
          title: guide?.metaTitle || meta.home.title,
          description: guide?.metaDescription || meta.home.description,
          url: frontUrl + router.asPath,
        }}
        additionalMetaTags={[
          {
            name: 'twitter:title',
            content: guide?.metaTitle || meta.home.title,
          },
          {
            name: 'twitter:description',
            content: guide?.metaDescription || meta.home.description,
          },
          {
            name: 'keywords',
            content: guide?.metaKeywords || '',
          },
        ]}
        canonical={frontUrl + router.asPath}
      />

      <Layout>
        {guide ? (
          <GuideItemView guide={guide} />
        ) : (
          <Loader size="sm" marginAuto />
        )}
      </Layout>
    </>
  );
};

export default GuideItem;
